import React, { useState } from "react";
import {
  Card,
  CardContent,
  Typography,
  Button,
  Box,
  CircularProgress,
  TextField,
  Chip,
  Alert,
  Grid,
  InputAdornment,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/styles";
import AddIcon from "@material-ui/icons/Add";
import SearchIcon from "@material-ui/icons/Search";
import DescriptionIcon from "@material-ui/icons/Description";
import ClearIcon from "@material-ui/icons/Clear";
import { format, isValid } from "date-fns";
import { es } from "date-fns/locale";
import { usePortafolioTarea } from "./hooks/usePortafolioTarea";
import PortafolioTareaCardList from "./PortafolioTareaCardList";
import AgregarPortafolioTarea from "./AgregarPortafolioTarea";
import { DatePicker, LocalizationProvider } from "@material-ui/lab";
import AdapterDateFns from "@material-ui/lab/AdapterDateFns";
import { Today } from "@material-ui/icons";

const useStyles = makeStyles((theme) => ({
  root: {
    marginTop: theme.spacing(3),
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(3),
    flexWrap: "wrap",
    gap: theme.spacing(2),
  },
  addButton: {
    borderRadius: 6,
    textTransform: "none",
    fontWeight: 500,
  },
  searchAndFilters: {
    marginBottom: theme.spacing(3),
    backgroundColor: "white",
    borderRadius: 8,
    padding: theme.spacing(2),
    boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
    border: `1px solid ${theme.palette.divider}`,
  },
  searchField: {
    "& .MuiOutlinedInput-root": {
      borderRadius: 6,
      "&:hover fieldset": {
        borderColor: theme.palette.primary.main,
      },
    },
  },
  dateField: {
    marginTop: theme.spacing(3),
  },

  statsContainer: {
    display: "flex",
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
    flexWrap: "wrap",
    alignItems: "center",
  },
  statChip: {
    fontWeight: 500,
  },
  loadingContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(6),
    gap: theme.spacing(2),
  },
  errorContainer: {
    marginBottom: theme.spacing(2),
  },
  emptyState: {
    textAlign: "center",
    padding: theme.spacing(6),
    backgroundColor: "white",
    borderRadius: 8,
    border: `1px solid ${theme.palette.divider}`,
  },
  emptyStateIcon: {
    fontSize: 64,
    color: theme.palette.grey[300],
    marginBottom: theme.spacing(2),
  },
  clearButton: {
    minWidth: "auto",
    padding: theme.spacing(1),
  },
}));

// Función para convertir formato de fecha
const convertirFechaParaFiltro = (fechaPortafolio) => {
  if (!fechaPortafolio) return "";
  const partes = fechaPortafolio.split("/");
  if (partes.length >= 2) {
    return `${partes[1]}-${partes[0]}`;
  }
  return "";
};

export default function PortafolioTarea({ tarea }) {
  const classes = useStyles();
  const [agregarPortafolio, setAgregarPortafolio] = useState(false);
  const [portafolioSeleccionado, setPortafolioSeleccionado] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterDate, setFilterDate] = useState(null);

  const {
    portafolios,
    loading,
    error,
    deleteFile,
    deletingFile,
    deletePortafolio,
  } = usePortafolioTarea(tarea?.id);

  // Filtrar portafolios
  const filteredPortafolios = portafolios.filter((portafolio) => {
    const matchesSearch = portafolio.descripcion
      .toLowerCase()
      .includes(searchTerm.toLowerCase());

    const fechaPortafolioFormateada = convertirFechaParaFiltro(
      portafolio.fecha,
    );
    const matchesDate =
      !filterDate ||
      !isValid(filterDate) ||
      fechaPortafolioFormateada === format(filterDate, "yyyy-MM");

    return matchesSearch && matchesDate;
  });

  const handleAgregarPortafolio = () => {
    setPortafolioSeleccionado(null);
    setAgregarPortafolio(true);
  };

  const handleEditarPortafolio = (portafolio) => {
    setPortafolioSeleccionado(portafolio);
    setAgregarPortafolio(true);
  };

  const handleCloseModal = () => {
    setAgregarPortafolio(false);
    setPortafolioSeleccionado(null);
  };

  const handleClearFilters = () => {
    setSearchTerm("");
    setFilterDate(null);
  };

  return (
    <Card className={classes.root}>
      <CardContent>
        {/* Header */}
        <Box className={classes.header}>
          <Typography variant="h6" component="h2">
            Bitácora de la Tarea
          </Typography>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleAgregarPortafolio}
            className={classes.addButton}
          >
            Agregar Entrada
          </Button>
        </Box>

        {/* Barra de búsqueda y filtros */}
        <Box className={classes.searchAndFilters}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                placeholder="Buscar en descripciones..."
                variant="outlined"
                size="small"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={classes.searchField}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12} sm={4} md={3}>
              <LocalizationProvider dateAdapter={AdapterDateFns} locale={es}>
                <DatePicker
                  views={["year", "month"]}
                  label="Filtrar por mes"
                  value={filterDate}
                  onChange={setFilterDate}
                  maxDate={new Date()}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      fullWidth
                      className={classes.dateField}
                    />
                  )}
                  openTo="month"
                  inputFormat="MM/yyyy"
                />
              </LocalizationProvider>
            </Grid>

            <Grid item xs={6} sm={2} md={2}>
              <Button
                fullWidth
                variant="outlined"
                size="small"
                onClick={handleClearFilters}
                startIcon={<ClearIcon />}
                disabled={!searchTerm && !filterDate}
              >
                Limpiar
              </Button>
            </Grid>

            <Grid item xs={6} sm={12} md={3}>
              <Button
                fullWidth
                variant="contained"
                color="primary"
                className={classes.addButton}
                onClick={handleAgregarPortafolio}
                disabled={loading}
                startIcon={<AddIcon />}
              >
                Nuevo Portafolio
              </Button>
            </Grid>
          </Grid>

          {/* Estadísticas */}
          {portafolios.length > 0 && (
            <Box className={classes.statsContainer}>
              <Chip
                icon={<DescriptionIcon />}
                label={`${portafolios.length} registros`}
                className={classes.statChip}
                size="small"
              />
              <Chip
                icon={<Today />}
                label={`${filteredPortafolios.length} mostrados`}
                className={classes.statChip}
                size="small"
              />
              {searchTerm && (
                <Chip
                  icon={<SearchIcon />}
                  label={`Búsqueda: "${searchTerm}"`}
                  className={classes.statChip}
                  size="small"
                  onDelete={() => setSearchTerm("")}
                />
              )}
              {filterDate && (
                <Chip
                  icon={<Today />}
                  label={`Mes: ${
                    filterDate && isValid(filterDate)
                      ? format(filterDate, "MMMM yyyy", { locale: es })
                      : "Fecha inválida"
                  }`}
                  className={classes.statChip}
                  size="small"
                  onDelete={() => setFilterDate("")}
                />
              )}
            </Box>
          )}
        </Box>

        {/* Error */}
        {error && (
          <div className={classes.errorContainer}>
            <Alert severity="error">{error}</Alert>
          </div>
        )}

        {/* Loading */}
        {loading ? (
          <div className={classes.loadingContainer}>
            <CircularProgress />
            <Typography variant="body1" color="textSecondary">
              Cargando bitácora...
            </Typography>
          </div>
        ) : filteredPortafolios.length === 0 && portafolios.length === 0 ? (
          /* Empty State - Sin portafolios */
          <div className={classes.emptyState}>
            <DescriptionIcon className={classes.emptyStateIcon} />
            <Typography variant="h6" gutterBottom>
              No hay entradas registradas
            </Typography>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              Comienza creando tu primer registro de actividades
            </Typography>
            <Button
              variant="contained"
              color="primary"
              className={classes.addButton}
              onClick={handleAgregarPortafolio}
              startIcon={<AddIcon />}
              style={{ marginTop: 16 }}
            >
              Crear Primera Entrada
            </Button>
          </div>
        ) : filteredPortafolios.length === 0 ? (
          /* Empty State - Con filtros activos */
          <div className={classes.emptyState}>
            <SearchIcon className={classes.emptyStateIcon} />
            <Typography variant="h6" gutterBottom>
              No se encontraron resultados
            </Typography>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              Intenta ajustar los filtros de búsqueda
            </Typography>
            <Button
              variant="outlined"
              color="primary"
              onClick={handleClearFilters}
              style={{ marginTop: 16 }}
            >
              Limpiar Filtros
            </Button>
          </div>
        ) : (
          /* Lista de Portafolios */
          <PortafolioTareaCardList
            portafolios={filteredPortafolios}
            tarea={tarea}
            onEditarPortafolio={handleEditarPortafolio}
            onDeleteFile={deleteFile}
            deletingFile={deletingFile}
            onDeletePortafolio={deletePortafolio}
          />
        )}

        {/* Modal Agregar/Editar */}
        <AgregarPortafolioTarea
          open={agregarPortafolio}
          onClose={handleCloseModal}
          tarea={tarea}
          portafolioSeleccionado={portafolioSeleccionado}
        />
      </CardContent>
    </Card>
  );
}
